import { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { appTheme } from './design-system';
import {
  initializeAuthSession,
  loadStoredAccount,
  signOutAccount,
  storeAccount,
  subscribeToAccountChanges,
} from './lib/authSession';
import LivePhotoCapturePage from './pages/LivePhotoCapturePage';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Portal from './pages/Portal';
import {
  AUTH_ROUTES,
  LIVE_PHOTO_ROUTE,
  PORTAL_ROUTES,
  getDefaultRouteForAccount,
} from './routes';
import './App.css';

function ProtectedRoute({ account }) {
  if (!account) {
    return <Navigate replace to={AUTH_ROUTES.login} />;
  }

  return <Outlet />;
}

function PublicOnlyRoute({ account }) {
  if (account) {
    return <Navigate replace to={PORTAL_ROUTES.trips} />;
  }

  return <Outlet />;
}

function App() {
  const [account, setAccount] = useState(() => loadStoredAccount());

  useEffect(() => {
    initializeAuthSession();
    return subscribeToAccountChanges(setAccount);
  }, []);

  function handleAccountUpdated(nextAccount) {
    storeAccount(nextAccount);
  }

  function handleSignOut() {
    void signOutAccount();
  }

  return (
    <ThemeProvider theme={appTheme}>
      <Routes>
        <Route element={<LandingPage account={account} />} path={AUTH_ROUTES.landing} />
        <Route element={<LivePhotoCapturePage />} path={LIVE_PHOTO_ROUTE} />

        <Route element={<PublicOnlyRoute account={account} />}>
          <Route
            element={<Login mode="login" onAuthenticated={handleAccountUpdated} />}
            path={AUTH_ROUTES.login}
          />
          <Route
            element={<Login mode="signup" onAuthenticated={handleAccountUpdated} />}
            path={AUTH_ROUTES.signup}
          />
          <Route
            element={<Login mode="reset" onAuthenticated={handleAccountUpdated} />}
            path={AUTH_ROUTES.resetPassword}
          />
        </Route>

        <Route element={<ProtectedRoute account={account} />}>
          <Route element={<Navigate replace to={PORTAL_ROUTES.trips} />} path={PORTAL_ROUTES.root} />
          <Route
            element={<Portal account={account} onAccountUpdated={handleAccountUpdated} onSignOut={handleSignOut} />}
            path={`${PORTAL_ROUTES.root}/*`}
          />
        </Route>

        <Route element={<Navigate replace to={getDefaultRouteForAccount(account)} />} path="*" />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
